"use client";

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Send, Bot, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { prophetsApi, Prophet, ProphetMessage } from "@/lib/api/prophets";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ProphetImagePlaceholder } from "@/components/prophets/ProphetImagePlaceholder";
import { Markdown } from "@/components/ui/markdown";
import { cn } from "@/lib/utils";

export function ProphetChat({
  prophet,
  className,
  headerRight,
  initialPrompt,
  onInitialPromptHandled,
}: {
  prophet: Prophet;
  className?: string;
  headerRight?: React.ReactNode;
  initialPrompt?: string | null;
  onInitialPromptHandled?: () => void;
}) {
  const t = useTranslations("Prophet");
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<ProphetMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState("");

  const sendMessage = useMutation({
    mutationFn: (message: string) =>
      prophetsApi.chat(prophet.id, { conversation_id: conversationId, message }),
    onSuccess: (res) => {
      setConversationId(res.conversation_id);
      setMessages((prev) => [...prev, { role: "assistant", content: res.reply }]);
      if (res.has_data_changes) {
        queryClient.invalidateQueries({ queryKey: ["portfolio"] });
        queryClient.invalidateQueries({ queryKey: ["accounts"] });
        queryClient.invalidateQueries({ queryKey: ["properties"] });
        queryClient.invalidateQueries({ queryKey: ["goals"] });
      }
      if (res.budget_exceeded) {
        toast.error(t("budgetExceeded"));
      }
    },
    onError: (error: unknown) => {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      toast.error(detail ?? t("toastError"));
    },
  });

  useEffect(() => {
    if (initialPrompt && initialPrompt.trim()) {
      const prompt = initialPrompt.trim();
      setMessages([{ role: "user", content: prompt }]);
      sendMessage.mutate(prompt);
      if (onInitialPromptHandled) {
        onInitialPromptHandled();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || sendMessage.isPending) return;
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    sendMessage.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className={cn(
        "flex h-[calc(100vh-10rem)] flex-col rounded-lg border border-border bg-surface",
        className
      )}
    >
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-surface-2">
          {prophet.image_url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={prophet.image_url} alt={prophet.name} className="h-full w-full object-cover" />
          ) : (
            <ProphetImagePlaceholder className="h-full w-full rounded-full" />
          )}
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-text">{prophet.name}</p>
        </div>
        {headerRight}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <p className="text-sm text-muted">{t("emptyState", { name: prophet.name })}</p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={cn("flex gap-2", m.role === "user" ? "justify-end" : "justify-start")}
          >
            {m.role === "assistant" && (
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-2">
                <Bot className="h-4 w-4 text-text" />
              </div>
            )}
            <div
              className={cn(
                "max-w-[75%] rounded-lg px-3 py-2 text-sm",
                m.role === "user"
                  ? "whitespace-pre-wrap bg-primary text-primary-foreground"
                  : "bg-surface-2 text-text"
              )}
            >
              {m.role === "assistant" ? <Markdown>{m.content}</Markdown> : m.content}
            </div>
            {m.role === "user" && (
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-2">
                <UserIcon className="h-4 w-4 text-text" />
              </div>
            )}
          </div>
        ))}
        {sendMessage.isPending && (
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-2">
              <Bot className="h-4 w-4 text-text" />
            </div>
            <div className="rounded-lg bg-surface-2 px-3 py-2 text-sm text-muted">
              {t("thinking")}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-end gap-2 border-t border-border p-3">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("inputPlaceholder")}
          className="min-h-10 flex-1 resize-none"
          rows={1}
        />
        <Button
          onClick={handleSend}
          disabled={sendMessage.isPending || !input.trim()}
          size="icon"
          aria-label={t("send")}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
